import React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { formatNumber, formatPercent } from "../FinancialReview/format";
import { KpiCard } from "../FinancialReview/ui";

export default function RetentionTab({ data, loading, error }) {
  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading Retention report…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return null;

  const { summary, start_month, end_month, series = [] } = data;
  const last = series[series.length - 1];
  const vsMonth = last?.previous_month;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Retention</h2>
        <p className="text-sm text-muted-foreground">
          {start_month} through {end_month}. Retention rate is returned licensed users divided by the prior month.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard label="Licensed" value={formatNumber(summary.licensed)} sublabel={end_month} />
        <KpiCard label="Returned" value={formatNumber(summary.returned)} sublabel={vsMonth ? `From ${vsMonth}` : undefined} />
        <KpiCard label="Added" value={formatNumber(summary.added)} sublabel={end_month ? `New in ${end_month}` : undefined} />
        <KpiCard label="Dropped" value={formatNumber(summary.dropped)} sublabel={vsMonth ? `Gone after ${vsMonth}` : undefined} />
        <KpiCard label="Retention rate" value={formatPercent(summary.return_rate)} sublabel={vsMonth ? `Of ${vsMonth}` : undefined} />
      </div>

      {series.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Month-over-month retention rate</CardTitle>
            <CardDescription>Share of the prior month’s licensed users who were licensed again</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {series.length > 1 && (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      domain={[0, 1]}
                      tickFormatter={(value) => formatPercent(value, 0)}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        name === "return_rate" ? formatPercent(value) : formatNumber(value),
                        name === "return_rate" ? "Retention rate" : name,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="return_rate"
                      name="Retention rate"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      connectNulls={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>vs</TableHead>
                    <TableHead>Licensed</TableHead>
                    <TableHead>Returned</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead>Dropped</TableHead>
                    <TableHead>Retention rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {series.map((row) => (
                    <TableRow key={row.month}>
                      <TableCell className="whitespace-nowrap">{row.month}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.previous_month}</TableCell>
                      <TableCell>{formatNumber(row.licensed)}</TableCell>
                      <TableCell>{formatNumber(row.returned)}</TableCell>
                      <TableCell>{formatNumber(row.added)}</TableCell>
                      <TableCell>{formatNumber(row.dropped)}</TableCell>
                      <TableCell>{formatPercent(row.return_rate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
